import React, { PropsWithChildren } from "react";
import PropTypes from "prop-types";
import {
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Checkbox,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { sanitizeListRestProps, useListContext } from "ra-core";
import classnames from "classnames";
import { useTimeout } from "react-admin";

const useStylesPlaceholder = makeStyles(
  (theme) => ({
    root: {
      backgroundColor: theme.palette.grey[300],
      display: "flex",
    },
  }),
  { name: "RaPlaceholder" }
);

const Placeholder = (props: { className?: string }) => {
  const classes = useStylesPlaceholder(props);
  return (
    <span className={classnames(classes.root, props.className)}>&nbsp;</span>
  );
};

const useStylesLoading = makeStyles(
  (theme) => ({
    primary: {
      width: "30vw",
      display: "inline-block",
      marginBottom: theme.spacing(),
    },
    tertiary: { float: "right", opacity: 0.541176, minWidth: "10vw" },
  }),
  { name: "RaSimpleListLoading" }
);

const times = (nbChildren: number, fn: (key: number) => void) =>
  Array.from({ length: nbChildren }, (_, key) => fn(key));

const SimpleListLoading = (props: {
  classes: any;
  className?: string;
  hasLeftAvatarOrIcon?: boolean;
  hasRightAvatarOrIcon?: boolean;
  hasSecondaryText?: boolean;
  hasTertiaryText?: boolean;
  nbFakeLines?: number;
}) => {
  const {
    className,
    hasLeftAvatarOrIcon,
    hasRightAvatarOrIcon,
    hasSecondaryText,
    hasTertiaryText,
    nbFakeLines = 5,
  } = props;
  const classes = useStylesLoading(props);
  const oneSecondHasPassed = useTimeout(1000);

  return oneSecondHasPassed ? (
    <List className={className}>
      {times(nbFakeLines, (key) => (
        <ListItem key={key}>
          {hasLeftAvatarOrIcon && (
            <ListItemAvatar>
              <Avatar>&nbsp;</Avatar>
            </ListItemAvatar>
          )}
          <ListItemText
            primary={
              <div>
                <Placeholder className={classes.primary} />
                {hasTertiaryText && (
                  <span className={classes.tertiary}>
                    <Placeholder />
                  </span>
                )}
              </div>
            }
            secondary={hasSecondaryText ? <Placeholder /> : undefined}
          />
          {hasRightAvatarOrIcon && (
            <ListItemSecondaryAction>
              <Avatar>&nbsp;</Avatar>
            </ListItemSecondaryAction>
          )}
        </ListItem>
      ))}
    </List>
  ) : null;
};

SimpleListLoading.propTypes = {
  className: PropTypes.string,
  hasLeftAvatarOrIcon: PropTypes.bool,
  hasRightAvatarOrIcon: PropTypes.bool,
  hasSecondaryText: PropTypes.bool,
  hasTertiaryText: PropTypes.bool,
  nbFakeLines: PropTypes.number,
};

const useStyles = makeStyles(
  {
    tertiary: { float: "right", opacity: 0.541176 },
  },
  { name: "RaSimpleList" }
);

const SelectSimpleList = (props: PropsWithChildren<SelectSimpleListProps>) => {
  const {
    className,
    classes: classesOverride,
    hasBulkActions,
    leftAvatar,
    leftIcon,
    linkType = "edit",
    primaryText,
    rightAvatar,
    rightIcon,
    secondaryText,
    tertiaryText,
    rowStyle,
    ...rest
  } = props;
  const {
    data,
    ids,
    loaded,
    total,
    onToggleItem,
    selectedIds,
  } = useListContext(props);
  const classes = useStyles(props);

  if (loaded === false) {
    return (
      <SimpleListLoading
        classes={classes}
        className={className}
        hasLeftAvatarOrIcon={!!leftIcon || !!leftAvatar}
        hasRightAvatarOrIcon={!!rightIcon || !!rightAvatar}
        hasSecondaryText={!!secondaryText}
        hasTertiaryText={!!tertiaryText}
      />
    );
  }

  const isSelected = (id: string | number) => selectedIds.includes(id);

  return total > 0 ? (
    <List className={className} {...sanitizeListRestProps(rest)}>
      {ids.map((id, rowIndex) => (
        <ListItem
          button={!!linkType}
          style={rowStyle ? rowStyle(data[id], rowIndex) : undefined}
          onClick={() => onToggleItem(id)}
          key={id}>
          <Checkbox
            checked={isSelected(id)}
            color="primary"
            style={{ paddingLeft: 0, paddingRight: "16px" }}
            inputProps={{ "aria-label": "primary checkbox" }}
          />
          {leftIcon && <ListItemIcon>{leftIcon(data[id], id)}</ListItemIcon>}
          {leftAvatar && (
            <ListItemAvatar>
              <Avatar>{leftAvatar(data[id], id)}</Avatar>
            </ListItemAvatar>
          )}
          <ListItemText
            primary={
              <div>
                {primaryText(data[id], id)}
                {tertiaryText && (
                  <span className={classes.tertiary}>
                    {tertiaryText(data[id], id)}
                  </span>
                )}
              </div>
            }
            secondary={secondaryText && secondaryText(data[id], id)}
          />
          {(rightAvatar || rightIcon) && (
            <ListItemSecondaryAction>
              {rightAvatar && <Avatar>{rightAvatar(data[id], id)}</Avatar>}
              {rightIcon && (
                <ListItemIcon>{rightIcon(data[id], id)}</ListItemIcon>
              )}
            </ListItemSecondaryAction>
          )}
        </ListItem>
      ))}
    </List>
  ) : null;
};

SelectSimpleList.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object,
  leftAvatar: PropTypes.func,
  leftIcon: PropTypes.func,
  linkType: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
    PropTypes.func,
  ]),
  primaryText: PropTypes.func,
  rightAvatar: PropTypes.func,
  rightIcon: PropTypes.func,
  secondaryText: PropTypes.func,
  tertiaryText: PropTypes.func,
  rowStyle: PropTypes.func,
};

type SelectSimpleListProps = {
  className?: string;
  classes?: any;
  hasBulkActions?: boolean;
  leftAvatar?: (record: any, id: string | number) => React.ReactNode;
  leftIcon?: (record: any, id: string | number) => React.ReactNode;
  linkType?: "edit" | "show" | ((record: any, id: string | number) => string);
  primaryText: (record: any, id: string | number) => React.ReactNode;
  rightAvatar?: (record: any, id: string | number) => React.ReactNode;
  rightIcon?: (record: any, id: string | number) => React.ReactNode;
  secondaryText?: (record: any, id: string | number) => React.ReactNode;
  tertiaryText?: (record: any, id: string | number) => React.ReactNode;
  rowStyle?: (record: any, index: number) => React.CSSProperties;
};

export default SelectSimpleList;
